<script setup lang="ts">
    import { computed, ref, toRef } from 'vue'
    import type { PropType } from 'vue'
    import type { CriterionTemplateReadModel } from "@Criteria/Hierarchies/Domain/readmodels"
    import type { AttributeLibraryViewModel } from "@Criteria/Hierarchies/Components/AttributeLibrary"

    // ---------------- props / emits ----------------

    const props = defineProps({
        viewModel: {
            type: Object as PropType<AttributeLibraryViewModel>,
            required: true
        }
    })

    const emit = defineEmits<{
        (e: 'create-criterion', parentId: number, attributeId: number, name: string): void
    }>()

    // ---------------- state ----------------

    const vm = toRef(props, 'viewModel')

    const selectedCategory = ref<string | null>(null)

    // ---------------- computed ----------------

    const opened = computed(() => vm.value.isOpen)
    const initialized = computed(() => vm.value.data != null)

    const categories = computed(() => vm.value.data?.Categories ?? [])
    const templates = computed(() => vm.value.data?.CriteriaTemplates ?? [])

    const templatesView = computed(() =>
        selectedCategory.value
            ? templates.value.filter(a => a.Category === selectedCategory.value)
            : templates.value
    )

    const categoryLabel = computed(() =>
        selectedCategory.value
            ? `Section: ${selectedCategory.value}`
            : "All attributes"
    )

    // ---------------- actions ----------------

    function emitCreateCriterion(t: CriterionTemplateReadModel)
    {
        emit("create-criterion", vm.value.parentId!, t.AttributeId, t.Name)
    }

    function close()
    {
        vm.value.close()
    }
</script>


<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-xl modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Choose attributes:</h5>
                        <button type="button" class="close" @click="close">&times;</button>
                    </div>

                    <div class="modal-body">
                        <h3 v-if="!initialized"><i class="fa fa-spinner fa-spin"></i> Loading... </h3>

                        <div v-if="initialized">

                            <div class="dropdown">
                                <button class="btn btn-outline-secondary dropdown-toggle" type="button" data-toggle="dropdown">
                                    <span>{{categoryLabel}}</span>
                                </button>
                                <div class="dropdown-menu">
                                    <a class="dropdown-item" @click="selectedCategory = null">All attributes</a>
                                    <h6 class="dropdown-header">Sections:</h6>
                                    <a class="dropdown-item" v-for="c in categories" :key="c" @click="selectedCategory = c">{{c}}</a>
                                </div>
                            </div>

                            <div class="row">
                                <div class="col-lg-4 col-md-4 col-sm-6 col-xs-12" v-for="item in templatesView" :key="item.AttributeId">
                                    <div class="card mt-3">
                                        <div class="card-header text-center">
                                            <span>{{item.Category || '&nbsp;'}}</span>
                                        </div>
                                        <div class="card-body" style="max-height: 250px; height: 250px; overflow-y: auto;">
                                            <h5 class="card-title">{{item.Name}}</h5>
                                            <p class="card-text">{{item.Description}}</p>
                                        </div>
                                        <div class="card-footer text-center">
                                            <button :disabled="item.Used" class="btn btn-outline-secondary" @click="emitCreateCriterion(item)">
                                                {{item.Used ? "Added" : "Create"}}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="modal-footer">
                        <button @click="close" type="button" class="btn btn-outline-secondary">Close</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
